import { Profile } from '@/shared/store/useAppStore'
import React, { memo } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { MAX_PROFILES_LIMIT } from '@/shared/constants'
import { PlusIcon } from '@/shared/ui'
import { useTranslation } from 'react-i18next'
import { useProfilesScreen } from '@/widgets/ProfileScreen/hooks/useProfilesScreen'
import { ProfileForm } from '@/widgets/ProfileScreen/components/ProfileForm'
import { WarningScreen } from '@/widgets/ProfileScreen/components/WarningScreen'
import ProfilesList from '@/widgets/ProfileScreen/components/ProfilesList'
import PinInputField from '@/shared/ui/PinInputField'

const RenderProfiles = memo(
    ({
        profiles,
        setProfiles,
        lastUsedProfile,
        setLastUsedProfile,
    }: {
        profiles: Profile[]
        setProfiles: (profiles: Profile[]) => void
        lastUsedProfile: Profile | null
        setLastUsedProfile: (profiles: Profile) => void
    }) => {
        const {
            handleOnDeleteProfile,
            handleOnEditProfile,
            handleCreateProfile,
            setEditMenuProfile,
            handleTypeNewProfile,
            isCreateProfileDisabled,
            showWarningMessage,
            lastUsedProfile: actualProfile,
            handleSetEditMenu,
            editMenuProfile,
            newProfile,
            handleSetNewProfile,
            handleAddProfile,
            cancelProfile,
            shouldCheckPin,
            handleCancelPin,
            pin,
            isPinError,
            handleOkEditProfile,
            handleEditProfileIsKidCheckbox,
            handleCheckPin,
            handleWarningScreenOkButton,
            handleSelectProfile,
            handleChangePin,
            handleSetShowWarningMessage,
        } = useProfilesScreen(
            profiles,
            setProfiles,
            lastUsedProfile,
            setLastUsedProfile
        )

        const { t: translate } = useTranslation()

        const shouldShowAddNewProfileButton =
            !editMenuProfile &&
            !newProfile?.order &&
            !shouldCheckPin.shouldCheck

        const shouldShowProfiles =
            !newProfile?.order &&
            !shouldCheckPin.shouldCheck &&
            !editMenuProfile

        const shouldShowEditProfileMenu =
            editMenuProfile?.profile_name !== undefined &&
            !shouldCheckPin.shouldCheck
        const isMaxProfilesLength = profiles.length >= MAX_PROFILES_LIMIT
        const containerStyle = {
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            width: '100%',
            maxHeight: '40vh',
        }

        const addProfileStyle = {
            marginTop: '12px',
            width: '100%',
            height: '36px',
            padding: '8px 12px',
            gap: '8px',
            borderRadius: '10px',
            bgcolor: isCreateProfileDisabled
                ? 'custom.lightGray'
                : 'primary.main',
        }
        return (
            <Box sx={containerStyle}>
                {shouldCheckPin.shouldCheck && (
                    <PinInputField
                        onChangePin={handleChangePin}
                        onCheckPin={() =>
                            handleCheckPin(pin, shouldCheckPin.id)
                        }
                        onCancelPin={handleCancelPin}
                        isPinError={!!isPinError}
                    />
                )}
                {newProfile?.order && !shouldCheckPin.shouldCheck && (
                    <ProfileForm
                        profile={newProfile}
                        setProfile={handleTypeNewProfile}
                        setCheckBox={() => handleSetNewProfile(newProfile)}
                        isCheckboxChecked={newProfile?.isKid}
                        isCreateProfileDisabled={isCreateProfileDisabled}
                        handleProfileAction={handleCreateProfile}
                        cancelAction={cancelProfile}
                        actionButtonText="add"
                    />
                )}
                {shouldShowEditProfileMenu && (
                    <ProfileForm
                        profile={editMenuProfile}
                        setProfile={handleSetEditMenu}
                        isCheckboxChecked={editMenuProfile?.isKid}
                        setCheckBox={() =>
                            handleEditProfileIsKidCheckbox(editMenuProfile)
                        }
                        isCreateProfileDisabled={false}
                        handleProfileAction={() =>
                            handleOkEditProfile(editMenuProfile)
                        }
                        cancelAction={() => setEditMenuProfile(null)}
                        actionButtonText="accept"
                    />
                )}
                {shouldShowProfiles && (
                    <ProfilesList
                        profiles={profiles}
                        actualProfileId={actualProfile?.id}
                        onEditProfile={handleOnEditProfile}
                        onDeleteProfile={handleOnDeleteProfile}
                        onSelectProfile={handleSelectProfile}
                    />
                )}
                {shouldShowAddNewProfileButton && (
                    <Button
                        disabled={isCreateProfileDisabled}
                        sx={addProfileStyle}
                        onClick={handleAddProfile}>
                        <PlusIcon fill="#272727" />
                        <Typography color="black" variant="label4">
                            {isMaxProfilesLength
                                ? translate(
                                      'maxProfilesCountReachedInfoMessage'
                                  )
                                : translate('createNewProfile')}
                        </Typography>
                    </Button>
                )}
                {showWarningMessage.name && (
                    <WarningScreen
                        message={` ${translate('globalDelete')} ${
                            showWarningMessage.name
                        }`}
                        onConfirm={handleWarningScreenOkButton}
                        onCancel={handleSetShowWarningMessage}
                    />
                )}
            </Box>
        )
    }
)

RenderProfiles.displayName = 'RenderProfiles'
export { RenderProfiles }
